/**
 * The two languages the product ships in (contracts.md "Локализация
 * клиента"): every user-facing sentence is a method, so a missing
 * translation is a type error, not a fallback. The desktop's i18n.rs is the
 * pattern; this is its web twin with the strings F1–F3 need.
 *
 * Service-message wording lives here and not on the wire (D71): the server
 * sends the fact, the client writes the sentence in the reader's language.
 */
import type { TtlUnit } from "./ttl";

export type Lang = "ru" | "en";

export function detectLang(): Lang {
  const prefs = navigator.languages ?? [navigator.language];
  for (const p of prefs) {
    if (p.toLowerCase().startsWith("ru")) return "ru";
    if (p.toLowerCase().startsWith("en")) return "en";
  }
  return "en";
}

/** `m:ss` — the only countdown either language spells the same way. */
function clock(seconds: number): string {
  const safe = Math.max(0, Math.floor(seconds));
  return `${Math.floor(safe / 60)}:${String(safe % 60).padStart(2, "0")}`;
}

/** Russian needs three plural forms; English two. */
function plural(lang: Lang, n: number, one: string, few: string, many: string): string {
  if (lang === "en") return n === 1 ? one : many;
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
  return many;
}

export interface Dict {
  // Login / register
  loginTitle(): string;
  loginUsername(): string;
  loginPassword(): string;
  loginEmail(): string;
  loginSubmit(): string;
  registerSubmit(): string;
  loginSwitchToRegister(): string;
  loginSwitchToLogin(): string;
  loginAgeGate(): string;
  loginFailed(reason: string): string;
  loginForgot(): string;

  // Confirming an address — the six digits, and the two flows that spend them
  // (gateway/src/confirm.rs).
  codeTitle(): string;
  /** No address in it: an address is never shown (owner rule, 21.09). */
  codeSent(): string;
  codeField(): string;
  codeSubmit(): string;
  codeResend(): string;
  /** «Send it again» is not ready yet: the letter went out `seconds` ago. */
  codeResendIn(seconds: number): string;
  /** How much of the five minutes is left, as m:ss. */
  codeExpiresIn(seconds: number): string;
  codeExpired(): string;
  codeBad(): string;
  codeBurnt(): string;
  codeBack(): string;
  recoverTitle(): string;
  recoverLead(): string;
  recoverSubmit(): string;
  recoverNewPassword(): string;
  recoverRepeatPassword(): string;
  recoverSave(): string;
  recoverDone(): string;
  passwordsDiffer(): string;
  /** The server has no relay (501), or is older than the whole flow (404). */
  mailUnavailable(): string;
  /** D311: signed in, but nothing opens until the account proves an address. */
  addressLead(): string;
  addressSubmit(): string;
  addressSignOut(): string;

  // Profile
  profileOpen(): string;
  profileChangePhoto(): string;
  profileDisplayName(): string;
  profileBio(): string;
  profileChangePassword(): string;
  profileChangeEmail(): string;

  // Changing the e-mail (D312): a code to the address on record, then one to
  // the new address. The code field, its countdown and its two refusals are
  // the `code*` keys above.
  emailTitle(): string;
  /** The label over the masked address on record. */
  emailCurrent(): string;
  /** An account with no address on record (a stand whose gate is off). */
  emailNone(): string;
  emailCurrentLead(): string;
  emailRequestCode(): string;
  /** The request button while the minute since the last letter runs. */
  emailRequestCodeIn(seconds: number): string;
  emailSentCurrent(): string;
  emailNext(): string;
  emailNew(): string;
  emailNewLead(): string;
  emailSentNew(): string;
  emailSave(): string;
  /** The toast at the end. */
  emailChanged(): string;
  /** 409: somebody else's address, or the one already on record. */
  emailTaken(): string;
  emailBad(): string;
  /** 429 without `field: "code"`: the per-address or per-connection budget. */
  emailTooMany(): string;
  /** The ten minutes the old mailbox's code bought ran out. */
  emailChangeExpired(): string;
  /** A gateway older than D312 answered 404: not a mail problem. */
  emailChangeUnsupported(): string;

  // Shell / sidebar
  channels(): string;
  allTab(): string;
  friendsTab(): string;
  searchPlaceholder(): string;
  newChannel(): string;
  /** A group (D274): a channel private from birth and for good. */
  newGroup(): string;
  /** A gateway older than groups answered `POST /v1/groups` with 404 and made nothing. */
  groupUnsupported(): string;
  members(n: number): string;
  connecting(): string;
  reconnecting(): string;
  offline(): string;

  // Feed
  today(): string;
  yesterday(): string;
  unreadSeparator(): string;
  edited(): string;
  unsupportedMessage(): string;
  unsupportedFormatting(): string;
  unsupportedAttachment(): string;
  loadOlder(): string;
  emptyChannel(): string;

  // Composer
  composerPlaceholder(): string;
  /** The heading over the composer's `@` list (dioxus-plan.md D284) — the
   * desktop's `channel_members`. */
  mentionHeading(): string;
  composerJoinFirst(): string;
  joinChannel(): string;
  slowModeWait(seconds: number): string;
  messageTooLongSplit(parts: number): string;
  send(): string;
  recordingCancelled(): string;
  /** «Send as picture / send as file» on a staged picture (D233). */
  sendAs(): string;
  sendAsPicture(): string;
  sendAsPictureHint(): string;
  sendAsFile(): string;
  sendAsFileHint(maxBytes: number): string;
  originalOverLimit(maxBytes: number): string;
  typing(names: string[]): string;

  // Auto-deleted messages (docs/disappearing-messages.md D296–D303). A span of
  // time is said three ways, and `lib/ttl.ts` decides the unit and the number
  // for all of them: `ttlLong` in words («30 секунд») for the menu,
  // `ttlShort` as a label («30 с», «1 м» — one letter for every unit, the
  // owner's word, because it is a mark on a button), `ttlHoursLeft` for a
  // countdown still an hour or more away — under the hour it is `m:ss`, which
  // has no words. `ttlAfter` is `ttlLong` inside a sentence, after «через»,
  // which takes the accusative — «через 1 минуту» — where the menu's own
  // row reads «1 минута»; the desktop's `ttl_long` and `ttl_after`.
  ttlLong(n: number, unit: TtlUnit): string;
  ttlAfter(n: number, unit: TtlUnit): string;
  ttlShort(n: number, unit: TtlUnit): string;
  ttlHoursLeft(hours: number): string;
  /** The send button's tooltip, and the mark on its bottom edge, while a
   * timer is set. */
  ttlClockOn(lifetime: string): string;
  /** The box's placeholder while a timer is set, and what a message waiting
   * for its first reader says about itself (D302). */
  ttlPlaceholder(lifetime: string): string;
  ttlAfterRead(lifetime: string): string;
  /** The one row of the send button's own menu, which opens the lifetimes
   * beside it, and the row of that submenu that turns the timer off. */
  ttlAutoDelete(): string;
  ttlTurnOff(): string;
  /** A bubble's countdown as a tooltip, and the foot of a message's menu. */
  ttlDisappearsIn(left: string): string;
  /** A lifetime was asked for and the answer carries no deadline: a gateway
   * older than `ttl_s` ignored it, and the sender has to be told (D296). */
  ttlUnsupported(): string;

  // The user card (F3)
  cardAdd(): string;
  cardPending(): string;
  cardAccept(): string;
  cardDecline(): string;
  cardMessage(): string;
  cardUnfriend(): string;
  cardBlock(): string;
  cardUnblock(): string;
  cardPolicyClosed(): string;
  requestsTitle(): string;
  toastRequest(who: string): string;
  toastAccepted(who: string): string;
  /** A person's card (D282): copies their `/u/<id>` address into the web
   * client; the notice after it is `consoleInviteCopied`. */
  copyLink(): string;

  // Link cards (docs/link-previews.md D279). What already has its words
  // elsewhere is reused, not repeated: «Вступить» is `joinChannel`,
  // «Написать» `cardMessage`, «Заявка отправлена» `cardPending`, «Открыть
  // профиль» `profileOpen` and «Закрытый канал» `consolePrivate`.
  linkKindChannel(): string;
  linkKindGroup(): string;
  linkKindDm(): string;
  linkKindUser(): string;
  linkOpenChannel(): string;
  linkOpenGroup(): string;
  linkOpenChat(): string;
  linkAddFriend(): string;
  linkAcceptRequest(): string;
  /** The bar over the composer while the draft holds a link (D289): its
   * accent line while the page is still being read, the line under it being
   * `loading`. */
  linkPreview(): string;
  /** The bar's cross: the message goes without its card (D289). */
  linkPreviewDismiss(): string;
  /** Something asked for and not yet here — the desktop's `loading`. */
  loading(): string;

  // Calls (read-only, F3)
  ringIncoming(who: string, channel: string): string;
  ringJoinHint(): string;
  ringDecline(): string;
  callPill(n: number): string;

  // Presence (presence-plan.md D187, D269). The words the status dot carries
  // as its tooltip — the dot has one bit of resolution, and the sentence is
  // where the rest of the answer goes. `presenceInCallAt` is its own string
  // rather than `presenceInCall` with a blank in it: the server never says
  // where a call is, so a channel this client cannot see is named nowhere, and
  // "in a call in «»" would be a sentence with a hole in it.
  presenceOnline(): string;
  presenceAfk(): string;
  presenceDnd(): string;
  presenceInvisible(): string;
  presenceInCall(): string;
  presenceInCallAt(channel: string): string;

  // Voice (F4)
  voiceJoin(): string;
  voiceConnecting(): string;
  voiceKeyExchange(): string;
  voiceLiveRtt(ms: number): string;
  voiceMutedState(): string;
  voiceDeafenedState(): string;
  voicePoorLoss(pct: number): string;
  voicePoorRtt(ms: number): string;
  voiceMute(): string;
  voiceUnmute(): string;
  voiceDeafen(): string;
  voiceUndeafen(): string;
  voiceDisconnect(): string;
  /** R16, said where the code is shown: voice is E2EE, the text chat is not. */
  voiceE2eeNote(): string;
  voiceCode(): string;
  voiceMicDenied(): string;
  voiceUnsupported(): string;
  voiceDropped(): string;

  // Streams (F5)
  watchStream(): string;
  stopWatching(): string;
  streamOf(name: string): string;
  streamPaused(): string;
  shareVolume(): string;
  streamEndedStopped(): string;
  streamEndedSourceGone(): string;
  streamEndedLeft(): string;
  streamEndedAdmin(): string;

  // Channel console (F6)
  consoleTitle(name: string): string;
  consoleDone(): string;
  consoleCancel(): string;
  consoleAccess(): string;
  consolePrivate(): string;
  /** A group's access row — no switch, because nothing can open it (D274). */
  groupPrivate(): string;
  groupPrivateHint(): string;
  consoleLimits(): string;
  consoleVoiceLimit(s: number): string;
  consoleMediaLimit(mb: number): string;
  consoleCharsLimit(n: number): string;
  consoleSlowmode(s: number): string;
  consoleVoiceEnabled(): string;
  consoleInvite(): string;
  consoleInviteNone(): string;
  consoleInviteCreate(): string;
  consoleInviteCopy(): string;
  consoleInviteCopied(): string;
  consoleInviteRefresh(): string;
  consoleInviteRevoke(): string;
  consoleInviteTtl(): string;
  consoleAppearance(): string;
  consoleBgLight(): string;
  consoleBgDark(): string;
  consoleBgPick(): string;
  consoleBgRemove(): string;
  consoleBgSet(): string;
  consoleBgNone(): string;
  consoleBgWillRemove(): string;
  consoleBgBlur(n: number): string;
  consoleBgBrightness(n: number): string;
  consolePeople(): string;
  consoleRoleOwner(): string;
  consoleRoleAdmin(): string;
  consoleMakeAdmin(): string;
  consoleRevokeAdmin(): string;
  consoleMakeOwner(): string;
  consoleVoiceMute(): string;
  consoleVoiceUnmute(): string;
  consoleKick(): string;
  consoleBanDay(): string;
  consoleBanForever(): string;
  consoleBanned(): string;
  consoleBanUntil(when: string): string;
  consoleBanPermanent(): string;
  consoleUnban(): string;

  // Reports (F6)
  menuReport(): string;
  reportTitle(): string;
  reportReasonLabel(key: string): string;
  reportCommentPlaceholder(): string;
  reportSend(): string;
  reportSent(): string;
  reportsTitle(): string;
  reportsOpen(n: number): string;
  reportsShowResolved(): string;
  reportsRemove(): string;
  reportsDismiss(): string;
  reportsEmpty(): string;


  // Message menu
  menuReact(): string;
  menuReply(): string;
  menuEdit(): string;
  menuDelete(): string;
  /** The header over a reply that carries nothing readable about what it
   * answers (a gateway older than D273). */
  replyElsewhere(): string;
  /** The header over a reply to a message since deleted (D273). */
  replyDeleted(): string;

  // Service messages (D71) — sender first, then the object where one exists.
  svcCallStarted(who: string): string;
  svcCallLive(participants: number): string;
  svcCallEnded(duration: string | null): string;
  svcJoined(who: string): string;
  svcLeft(who: string): string;
  svcRemoved(who: string, whom: string): string;
  svcAdminGranted(who: string, whom: string): string;
  svcAdminRevoked(who: string, whom: string): string;
  svcRenamed(who: string, name: string): string;
  svcBanned(who: string, whom: string, until: string | null): string;
  svcUnbanned(who: string, whom: string): string;
  svcVoiceMuted(who: string, whom: string, until: string | null): string;
  svcVoiceUnmuted(who: string, whom: string): string;
  svcOwnerChanged(who: string, whom: string): string;
  svcUnknown(): string;
  /** Short, name-free sidebar/reply preview per service kind. */
  svcPreview(kind: string): string;

  // A link opened in the browser, offered to the desktop client (D313).
  desktopOfferTitle(): string;
  desktopOpen(): string;
  desktopOpening(): string;
  desktopNoApp(): string;
  desktopInstallTitle(): string;
  /** The try moved nothing: most likely there is no app to take the link. */
  desktopMissingTitle(): string;
  desktopStore(): string;
  desktopInstaller(): string;
  desktopHaveIt(): string;
  desktopDismiss(): string;

  // Durations
  durationS(totalSeconds: number): string;
}

const ru: Dict = {
  loginTitle: () => "EchoFloat",
  loginUsername: () => "Имя пользователя",
  loginPassword: () => "Пароль",
  loginEmail: () => "Почта",
  loginSubmit: () => "Войти",
  registerSubmit: () => "Создать аккаунт",
  loginSwitchToRegister: () => "Нет аккаунта? Регистрация",
  loginSwitchToLogin: () => "Уже есть аккаунт? Войти",
  loginAgeGate: () => "Мне есть 13 лет, и я принимаю правила",
  loginFailed: (reason) => reason,
  loginForgot: () => "Забыли пароль?",

  codeTitle: () => "Подтвердите почту",
  codeSent: () => "Мы отправили код на указанную почту",
  codeField: () => "Код из письма",
  codeSubmit: () => "Подтвердить",
  codeResend: () => "Отправить ещё раз",
  codeResendIn: (seconds) => `Отправить ещё раз можно через ${seconds} с`,
  codeExpiresIn: (seconds) => `Код действует ещё ${clock(seconds)}`,
  codeExpired: () => "Код истёк — запросите новый",
  codeBad: () => "Код неверный или истёк",
  codeBurnt: () => "Слишком много попыток. Начните заново.",
  codeBack: () => "Назад",
  recoverTitle: () => "Восстановление пароля",
  recoverLead: () => "Введите почту, на которую зарегистрирован аккаунт.",
  recoverSubmit: () => "Отправить код",
  recoverNewPassword: () => "Новый пароль",
  recoverRepeatPassword: () => "Повторите пароль",
  recoverSave: () => "Сохранить пароль",
  recoverDone: () => "Пароль изменён — войдите с новым.",
  passwordsDiffer: () => "Пароли не совпадают",
  mailUnavailable: () => "Этот сервер не умеет отправлять письма.",
  addressLead: () => "Чтобы продолжить, подтвердите почту — пришлём на неё код из шести цифр.",
  addressSubmit: () => "Отправить код",
  addressSignOut: () => "Выйти",
  profileOpen: () => "Открыть профиль",
  profileChangePhoto: () => "Изменить фото",
  profileDisplayName: () => "Имя в приложении",
  profileBio: () => "О себе",
  profileChangePassword: () => "Сменить пароль",
  profileChangeEmail: () => "Сменить почту",

  emailTitle: () => "Смена почты",
  emailCurrent: () => "Текущая почта",
  emailNone: () => "Почта не указана",
  emailCurrentLead: () => "Сначала подтвердите, что текущая почта ваша: пришлём на неё код.",
  emailRequestCode: () => "Запросить код",
  emailRequestCodeIn: (seconds) => `Запросить код можно через ${seconds} с`,
  emailSentCurrent: () => "Код отправлен на текущую почту",
  emailNext: () => "Далее",
  emailNew: () => "Новая почта",
  emailNewLead: () => "Теперь укажите новую почту — код придёт на неё.",
  emailSentNew: () => "Код отправлен на новую почту",
  emailSave: () => "Сменить почту",
  emailChanged: () => "Почта изменена",
  emailTaken: () => "Эта почта уже используется",
  emailBad: () => "Это не похоже на адрес почты",
  emailTooMany: () => "Слишком много запросов — попробуйте позже",
  emailChangeExpired: () => "Время на смену почты истекло — начните заново",
  emailChangeUnsupported: () => "Этот сервер пока не умеет менять почту — его нужно обновить.",

  channels: () => "Группы",
  allTab: () => "Все",
  friendsTab: () => "Друзья",
  searchPlaceholder: () => "Поиск",
  newChannel: () => "Новая группа",
  newGroup: () => "Новая группа",
  groupUnsupported: () => "Этот сервер пока не умеет создавать группы.",
  members: (n) => `${n} ${plural("ru", n, "участник", "участника", "участников")}`,
  connecting: () => "Подключение…",
  reconnecting: () => "Переподключение…",
  offline: () => "Нет соединения",

  today: () => "Сегодня",
  yesterday: () => "Вчера",
  unreadSeparator: () => "Непрочитанные",
  edited: () => "изменено",
  unsupportedMessage: () => "Обновите EchoFloat, чтобы открыть сообщение",
  unsupportedFormatting: () => "Часть оформления не показана",
  unsupportedAttachment: () => "Обновите EchoFloat, чтобы открыть вложение",
  loadOlder: () => "Ранее",
  emptyChannel: () => "Начните разговор",

  composerPlaceholder: () => "Сообщение",
  mentionHeading: () => "Участники",
  composerJoinFirst: () => "Вступите, чтобы писать",
  joinChannel: () => "Вступить",
  slowModeWait: (s) => `Подождите ${s} с`,
  messageTooLongSplit: (n) =>
    `Сообщение уйдёт ${n} ${plural("ru", n, "частью", "частями", "частями")}`,
  send: () => "Отправить",
  recordingCancelled: () => "Запись отменена",
  sendAs: () => "Отправить как",
  sendAsPicture: () => "Картинку",
  sendAsPictureHint: () => "1024 px, до 300 КиБ",
  sendAsFile: () => "Файл",
  sendAsFileHint: (b) => `Оригинал, до ${Math.floor(b / (1024 * 1024))} МиБ`,
  originalOverLimit: (b) =>
    `Оригиналом можно отправить файл до ${
      Math.floor(b / (1024 * 1024))
    } МиБ — этот больше, отправьте его картинкой`,
  typing: (names) =>
    names.length === 1
      ? `${names[0]} печатает…`
      : `${names.join(", ")} печатают…`,

  ttlLong: (n, unit) => {
    switch (unit) {
      case "d": return `${n} ${plural("ru", n, "день", "дня", "дней")}`;
      case "h": return `${n} ${plural("ru", n, "час", "часа", "часов")}`;
      case "m": return `${n} ${plural("ru", n, "минута", "минуты", "минут")}`;
      default: return `${n} ${plural("ru", n, "секунда", "секунды", "секунд")}`;
    }
  },
  // Only the two feminine singulars differ from the nominative: «1 минуту»,
  // «21 секунду». Days and hours are masculine, and every other count is the
  // genitive both ways.
  ttlAfter: (n, unit) => {
    switch (unit) {
      case "m": return `${n} ${plural("ru", n, "минуту", "минуты", "минут")}`;
      case "s": return `${n} ${plural("ru", n, "секунду", "секунды", "секунд")}`;
      default: return ru.ttlLong(n, unit);
    }
  },
  ttlShort: (n, unit) => {
    switch (unit) {
      case "d": return `${n} д`;
      case "h": return `${n} ч`;
      case "m": return `${n} м`;
      default: return `${n} с`;
    }
  },
  ttlHoursLeft: (h) => `${h} ч`,
  ttlClockOn: (lifetime) => `Сообщения удаляются через ${lifetime} после прочтения`,
  ttlPlaceholder: (lifetime) => `Удалится через ${lifetime} после прочтения`,
  ttlAfterRead: (lifetime) => `Удалится через ${lifetime} после прочтения`,
  ttlAutoDelete: () => "Автоудаляемое сообщение",
  ttlTurnOff: () => "Без автоудаления",
  ttlDisappearsIn: (left) => `Удалится через ${left}`,
  ttlUnsupported: () => "Сервер ещё не умеет автоудаление — это сообщение останется",

  cardAdd: () => "В друзья",
  cardPending: () => "Заявка отправлена",
  cardAccept: () => "Принять",
  cardDecline: () => "Отклонить",
  cardMessage: () => "Написать",
  cardUnfriend: () => "Удалить из друзей",
  cardBlock: () => "Заблокировать",
  cardUnblock: () => "Разблокировать",
  cardPolicyClosed: () => "Заявки закрыты",
  requestsTitle: () => "Заявки в друзья",
  toastRequest: (who) => `${who}: заявка в друзья`,
  toastAccepted: (who) => `${who}: заявка принята`,
  copyLink: () => "Скопировать ссылку",

  linkKindChannel: () => "Группа",
  linkKindGroup: () => "Группа",
  linkKindDm: () => "Личная переписка",
  linkKindUser: () => "Профиль",
  linkOpenChannel: () => "Открыть группу",
  linkOpenGroup: () => "Открыть группу",
  linkOpenChat: () => "Открыть чат",
  linkAddFriend: () => "Добавить в друзья",
  linkAcceptRequest: () => "Принять заявку",
  linkPreview: () => "Превью ссылки",
  linkPreviewDismiss: () => "Отправить без превью",
  loading: () => "Загрузка…",

  ringIncoming: (who, channel) => `${who} звонит в «${channel}»`,
  ringJoinHint: () => "Ответить",
  ringDecline: () => "Отклонить",
  callPill: (n) => `Звонок · ${n}`,

  presenceOnline: () => "Онлайн",
  presenceAfk: () => "AFK",
  presenceDnd: () => "Не беспокоить",
  presenceInvisible: () => "Невидимка",
  presenceInCall: () => "В звонке",
  presenceInCallAt: (channel) => `В звонке в «${channel}»`,

  voiceJoin: () => "Войти",
  voiceConnecting: () => "Соединяемся…",
  voiceKeyExchange: () => "Шифруем…",
  voiceLiveRtt: (ms) => `Микрофон включён · ${ms} мс`,
  voiceMutedState: () => "Микрофон выключен",
  voiceDeafenedState: () => "Звук отключён",
  voicePoorLoss: (pct) => `Плохое соединение · потери ${pct.toFixed(0)}%`,
  voicePoorRtt: (ms) => `Плохое соединение · ${ms} мс`,
  voiceMute: () => "Выключить микрофон",
  voiceUnmute: () => "Включить микрофон",
  voiceDeafen: () => "Выключить звук",
  voiceUndeafen: () => "Включить звук",
  voiceDisconnect: () => "Выйти",
  voiceE2eeNote: () => "Звонок защищён сквозным шифрованием. Чат — нет",
  voiceCode: () => "Код проверки",
  voiceMicDenied: () => "Разрешите доступ к микрофону",
  voiceUnsupported: () => "Обновите Chrome, чтобы звонить",
  voiceDropped: () => "Звонок оборвался",

  watchStream: () => "Смотреть стрим",
  stopWatching: () => "Закрыть стрим",
  streamOf: (name) => `Стрим ${name}`,
  streamPaused: () => "Стрим на паузе",
  shareVolume: () => "Громкость стрима",
  streamEndedStopped: () => "Стрим завершён",
  streamEndedSourceGone: () => "Окно закрыто",
  streamEndedLeft: () => "Автор вышел из звонка",
  streamEndedAdmin: () => "Администратор остановил стрим",

  consoleTitle: (name) => `«${name}»: настройки`,
  consoleDone: () => "Готово",
  consoleCancel: () => "Отмена",
  consoleAccess: () => "Доступ",
  consolePrivate: () => "Закрытая группа",
  groupPrivate: () => "Закрытая группа",
  groupPrivateHint: () => "Группу видят только её участники, и открытой она не станет.",
  consoleLimits: () => "Лимиты",
  consoleVoiceLimit: (s) => `Голосовое до ${s} с`,
  consoleMediaLimit: (mb) => `Файлы: до ${mb} МБ`,
  consoleCharsLimit: (n) => `Сообщение до ${n} символов`,
  consoleSlowmode: (s) => (s === 0 ? "Медленный режим выключен" : `Пауза между сообщениями: ${s} с`),
  consoleVoiceEnabled: () => "Звонки",
  consoleInvite: () => "Приглашение",
  consoleInviteNone: () => "Ссылки нет",
  consoleInviteCreate: () => "Создать",
  consoleInviteCopy: () => "Копировать",
  consoleInviteCopied: () => "Ссылка скопирована",
  consoleInviteRefresh: () => "Новая ссылка",
  consoleInviteRevoke: () => "Удалить",
  consoleInviteTtl: () => "Ссылка действует 3 дня. Новая заменит старую",
  consoleAppearance: () => "Оформление",
  consoleBgLight: () => "Фон в светлой теме",
  consoleBgDark: () => "Фон в тёмной теме",
  consoleBgPick: () => "Выбрать",
  consoleBgRemove: () => "Убрать",
  consoleBgSet: () => "установлен",
  consoleBgNone: () => "нет",
  consoleBgWillRemove: () => "будет убран",
  consoleBgBlur: (n) => `Размытие фона: ${n}`,
  consoleBgBrightness: (n) => `Яркость фона: ${n}`,
  consolePeople: () => "Участники",
  consoleRoleOwner: () => "владелец",
  consoleRoleAdmin: () => "админ",
  consoleMakeAdmin: () => "Сделать админом",
  consoleRevokeAdmin: () => "Убрать из админов",
  consoleMakeOwner: () => "Передать группу",
  consoleVoiceMute: () => "Выключить микрофон",
  consoleVoiceUnmute: () => "Включить микрофон",
  consoleKick: () => "Исключить",
  consoleBanDay: () => "Заблокировать на день",
  consoleBanForever: () => "Заблокировать навсегда",
  consoleBanned: () => "Заблокированные",
  consoleBanUntil: (when) => `до ${when}`,
  consoleBanPermanent: () => "навсегда",
  consoleUnban: () => "Разблокировать",

  menuReport: () => "Пожаловаться",
  reportTitle: () => "Жалоба",
  reportReasonLabel: (key) => {
    switch (key) {
      case "abuse": return "Оскорбления";
      case "sexual": return "Откровенный контент";
      case "child": return "Опасность для детей";
      case "violence": return "Насилие";
      case "illegal": return "Незаконное";
      case "spam": return "Спам";
      default: return "Другое";
    }
  },
  reportCommentPlaceholder: () => "Комментарий (необязательно)",
  reportSend: () => "Отправить",
  reportSent: () => "Жалоба отправлена",
  reportsTitle: () => "Жалобы",
  reportsOpen: (n) => `Открытых: ${n}`,
  reportsShowResolved: () => "Показать закрытые",
  reportsRemove: () => "Удалено",
  reportsDismiss: () => "Отклонить",
  reportsEmpty: () => "Жалоб нет",


  menuReact: () => "Реакция",
  menuReply: () => "Ответить",
  menuEdit: () => "Изменить",
  menuDelete: () => "Удалить",
  replyElsewhere: () => "Более раннее сообщение",
  replyDeleted: () => "Сообщение удалено",

  svcCallStarted: (who) => `${who} начинает звонок`,
  svcCallLive: (n) => `Идёт звонок · ${n} ${plural("ru", n, "участник", "участника", "участников")}`,
  svcCallEnded: (d) => (d ? `Звонок завершён · ${d}` : "Звонок завершён"),
  svcJoined: (who) => `${who} теперь в группе`,
  svcLeft: (who) => `${who} покидает группу`,
  svcRemoved: (who, whom) => `${who} исключает ${whom}`,
  svcAdminGranted: (who, whom) => `${who} назначает ${whom} администратором`,
  svcAdminRevoked: (who, whom) => `${who} снимает с ${whom} права администратора`,
  svcRenamed: (who, name) => `${who} переименовывает группу в «${name}»`,
  svcBanned: (who, whom, until) =>
    until ? `${who} блокирует ${whom} до ${until}` : `${who} блокирует ${whom}`,
  svcUnbanned: (who, whom) => `${who} снимает блокировку с ${whom}`,
  svcVoiceMuted: (who, whom, until) =>
    until
      ? `${who} выключает микрофон ${whom} до ${until}`
      : `${who} выключает микрофон ${whom}`,
  svcVoiceUnmuted: (who, whom) => `${who} возвращает ${whom} микрофон`,
  svcOwnerChanged: (who, whom) => `${who} передаёт группу ${whom}`,
  svcUnknown: () => "Обновите EchoFloat, чтобы увидеть событие",
  svcPreview: (kind) => {
    switch (kind) {
      case "call": return "Звонок";
      case "joined": return "Новый участник";
      case "left": return "Участник вышел";
      case "removed": return "Участник исключён";
      case "admin_granted": return "Новый администратор";
      case "admin_revoked": return "Администратор снят";
      case "renamed": return "Группа переименована";
      case "banned": return "Блокировка";
      case "unbanned": return "Блокировка снята";
      case "voice_muted": return "Микрофон выключен";
      case "voice_unmuted": return "Микрофон включён";
      case "owner_changed": return "Новый владелец";
      default: return "Событие";
    }
  },

  desktopOfferTitle: () => "Открыть в приложении EchoFloat?",
  desktopOpen: () => "Открыть в EchoFloat",
  desktopOpening: () => "Открываем EchoFloat…",
  desktopNoApp: () => "Нет приложения?",
  desktopInstallTitle: () => "EchoFloat для Windows: звонки и чаты в своём окне",
  desktopMissingTitle: () => "Похоже, EchoFloat не установлен",
  desktopStore: () => "Установить из Microsoft Store",
  desktopInstaller: () => "Скачать установщик",
  desktopHaveIt: () => "Уже установлен — открыть",
  desktopDismiss: () => "Остаться в браузере",

  durationS: (total) => {
    const m = Math.floor(total / 60);
    const s = total % 60;
    if (m === 0) return `${s} с`;
    return s === 0 ? `${m} мин` : `${m} мин ${s} с`;
  },
};

const en: Dict = {
  loginTitle: () => "EchoFloat",
  loginUsername: () => "Username",
  loginPassword: () => "Password",
  loginEmail: () => "Email",
  loginSubmit: () => "Sign in",
  registerSubmit: () => "Create account",
  loginSwitchToRegister: () => "No account? Register",
  loginSwitchToLogin: () => "Have an account? Sign in",
  loginAgeGate: () => "I am 13 or older and accept the terms",
  loginFailed: (reason) => reason,
  loginForgot: () => "Forgot password?",

  codeTitle: () => "Confirm your e-mail",
  codeSent: () => "We sent a code to the address you entered",
  codeField: () => "Code from the letter",
  codeSubmit: () => "Confirm",
  codeResend: () => "Send it again",
  codeResendIn: (seconds) => `You can ask again in ${seconds} s`,
  codeExpiresIn: (seconds) => `The code is good for another ${clock(seconds)}`,
  codeExpired: () => "The code has expired — ask for a new one",
  codeBad: () => "The code is wrong or has expired",
  codeBurnt: () => "Too many wrong codes. Start again.",
  codeBack: () => "Back",
  recoverTitle: () => "Password recovery",
  recoverLead: () => "Enter the e-mail your account is registered with.",
  recoverSubmit: () => "Send the code",
  recoverNewPassword: () => "New password",
  recoverRepeatPassword: () => "Repeat the password",
  recoverSave: () => "Save the password",
  recoverDone: () => "Password changed — sign in with the new one.",
  passwordsDiffer: () => "The passwords do not match",
  mailUnavailable: () => "This server cannot send e-mail.",
  addressLead: () => "To continue, confirm your e-mail — we will send a six-digit code to it.",
  addressSubmit: () => "Send code",
  addressSignOut: () => "Sign out",
  profileOpen: () => "Open profile",
  profileChangePhoto: () => "Change photo",
  profileDisplayName: () => "Name in the app",
  profileBio: () => "Bio",
  profileChangePassword: () => "Change password",
  profileChangeEmail: () => "Change e-mail",

  emailTitle: () => "Change e-mail",
  emailCurrent: () => "Current e-mail",
  emailNone: () => "No e-mail on record",
  emailCurrentLead: () =>
    "First confirm the current address is yours: we will send a code to it.",
  emailRequestCode: () => "Request code",
  emailRequestCodeIn: (seconds) => `You can request a code in ${seconds} s`,
  emailSentCurrent: () => "We sent a code to your current address",
  emailNext: () => "Next",
  emailNew: () => "New e-mail",
  emailNewLead: () => "Now enter the new address — the code goes to it.",
  emailSentNew: () => "We sent a code to the new address",
  emailSave: () => "Change e-mail",
  emailChanged: () => "E-mail changed",
  emailTaken: () => "This address is already in use",
  emailBad: () => "That does not look like an e-mail address",
  emailTooMany: () => "Too many requests — try again later",
  emailChangeExpired: () => "The e-mail change has expired — start again",
  emailChangeUnsupported: () => "This server cannot change e-mail yet — it needs an update.",

  channels: () => "Groups",
  allTab: () => "All",
  friendsTab: () => "Friends",
  searchPlaceholder: () => "Search",
  newChannel: () => "New group",
  newGroup: () => "New group",
  groupUnsupported: () => "This server can't create groups yet.",
  members: (n) => `${n} ${plural("en", n, "member", "members", "members")}`,
  connecting: () => "Connecting…",
  reconnecting: () => "Reconnecting…",
  offline: () => "No connection",

  today: () => "Today",
  yesterday: () => "Yesterday",
  unreadSeparator: () => "Unread",
  edited: () => "edited",
  unsupportedMessage: () => "Update EchoFloat to open this message",
  unsupportedFormatting: () => "Some formatting isn't shown",
  unsupportedAttachment: () => "Update EchoFloat to open this attachment",
  loadOlder: () => "Earlier",
  emptyChannel: () => "Start the conversation",

  composerPlaceholder: () => "Message",
  mentionHeading: () => "Members",
  composerJoinFirst: () => "Join to write",
  joinChannel: () => "Join",
  slowModeWait: (s) => `Wait ${s}s`,
  messageTooLongSplit: (n) => `Message will be sent in ${n} parts`,
  send: () => "Send",
  recordingCancelled: () => "Recording cancelled",
  sendAs: () => "Send as",
  sendAsPicture: () => "Picture",
  sendAsPictureHint: () => "1024 px, up to 300 KiB",
  sendAsFile: () => "File",
  sendAsFileHint: (b) => `The original, up to ${Math.floor(b / (1024 * 1024))} MiB`,
  originalOverLimit: (b) =>
    `An original may be at most ${
      Math.floor(b / (1024 * 1024))
    } MiB — this one is larger, so send it as a picture`,
  typing: (names) =>
    names.length === 1
      ? `${names[0]} is typing…`
      : `${names.join(", ")} are typing…`,

  ttlLong: (n, unit) => {
    switch (unit) {
      case "d": return `${n} ${plural("en", n, "day", "days", "days")}`;
      case "h": return `${n} ${plural("en", n, "hour", "hours", "hours")}`;
      case "m": return `${n} ${plural("en", n, "minute", "minutes", "minutes")}`;
      default: return `${n} ${plural("en", n, "second", "seconds", "seconds")}`;
    }
  },
  ttlAfter: (n, unit) => en.ttlLong(n, unit),
  ttlShort: (n, unit) => `${n}${unit}`,
  ttlHoursLeft: (h) => `${h}h`,
  ttlClockOn: (lifetime) => `Messages are deleted ${lifetime} after they are read`,
  ttlPlaceholder: (lifetime) => `Deleted ${lifetime} after it is read`,
  ttlAfterRead: (lifetime) => `Deleted ${lifetime} after it is read`,
  ttlAutoDelete: () => "Auto-deleted message",
  ttlTurnOff: () => "No auto-delete",
  ttlDisappearsIn: (left) => `Deleted in ${left}`,
  ttlUnsupported: () =>
    "This server does not support auto-deleted messages yet — this one will stay",

  cardAdd: () => "Add friend",
  cardPending: () => "Request sent",
  cardAccept: () => "Accept",
  cardDecline: () => "Decline",
  cardMessage: () => "Message",
  cardUnfriend: () => "Unfriend",
  cardBlock: () => "Block",
  cardUnblock: () => "Unblock",
  cardPolicyClosed: () => "Requests are closed",
  requestsTitle: () => "Friend requests",
  toastRequest: (who) => `${who}: friend request`,
  toastAccepted: (who) => `${who}: request accepted`,
  copyLink: () => "Copy link",

  linkKindChannel: () => "Group",
  linkKindGroup: () => "Group",
  linkKindDm: () => "Direct messages",
  linkKindUser: () => "Profile",
  linkOpenChannel: () => "Open group",
  linkOpenGroup: () => "Open group",
  linkOpenChat: () => "Open chat",
  linkAddFriend: () => "Add friend",
  linkAcceptRequest: () => "Accept request",
  linkPreview: () => "Link preview",
  linkPreviewDismiss: () => "Send without preview",
  loading: () => "Loading…",

  ringIncoming: (who, channel) => `${who} is calling in “${channel}”`,
  ringJoinHint: () => "Answer",
  ringDecline: () => "Decline",
  callPill: (n) => `Call · ${n}`,

  presenceOnline: () => "Online",
  presenceAfk: () => "AFK",
  presenceDnd: () => "Do not disturb",
  presenceInvisible: () => "Invisible",
  presenceInCall: () => "In a call",
  presenceInCallAt: (channel) => `In a call in “${channel}”`,

  voiceJoin: () => "Join",
  voiceConnecting: () => "Connecting…",
  voiceKeyExchange: () => "Securing…",
  voiceLiveRtt: (ms) => `Microphone on · ${ms} ms`,
  voiceMutedState: () => "Microphone off",
  voiceDeafenedState: () => "Sound is off",
  voicePoorLoss: (pct) => `Poor connection · ${pct.toFixed(0)}% loss`,
  voicePoorRtt: (ms) => `Poor connection · ${ms} ms`,
  voiceMute: () => "Mute",
  voiceUnmute: () => "Unmute",
  voiceDeafen: () => "Mute sound",
  voiceUndeafen: () => "Unmute sound",
  voiceDisconnect: () => "Leave",
  voiceE2eeNote: () => "The call is end-to-end encrypted. Chat isn't",
  voiceCode: () => "Verification code",
  voiceMicDenied: () => "Allow microphone access",
  voiceUnsupported: () => "Update Chrome to make calls",
  voiceDropped: () => "Call ended unexpectedly",

  watchStream: () => "Watch stream",
  stopWatching: () => "Close stream",
  streamOf: (name) => `${name}'s stream`,
  streamPaused: () => "Stream paused",
  shareVolume: () => "Stream volume",
  streamEndedStopped: () => "Stream ended",
  streamEndedSourceGone: () => "Window closed",
  streamEndedLeft: () => "Streamer left the call",
  streamEndedAdmin: () => "Admin stopped the stream",

  consoleTitle: (name) => `“${name}” settings`,
  consoleDone: () => "Done",
  consoleCancel: () => "Cancel",
  consoleAccess: () => "Access",
  consolePrivate: () => "Private group",
  groupPrivate: () => "Private group",
  groupPrivateHint: () => "Only its members can see a group, and it can't be made public.",
  consoleLimits: () => "Limits",
  consoleVoiceLimit: (s) => `Voice message up to ${s}s`,
  consoleMediaLimit: (mb) => `Files up to ${mb} MB`,
  consoleCharsLimit: (n) => `Message up to ${n} characters`,
  consoleSlowmode: (s) => (s === 0 ? "Slow mode: off" : `Slow mode: ${s} s`),
  consoleVoiceEnabled: () => "Calls",
  consoleInvite: () => "Invite",
  consoleInviteNone: () => "No link",
  consoleInviteCreate: () => "Create",
  consoleInviteCopy: () => "Copy",
  consoleInviteCopied: () => "Link copied",
  consoleInviteRefresh: () => "New link",
  consoleInviteRevoke: () => "Delete",
  consoleInviteTtl: () => "The link lasts 3 days. A new link replaces it",
  consoleAppearance: () => "Appearance",
  consoleBgLight: () => "Background in light theme",
  consoleBgDark: () => "Background in dark theme",
  consoleBgPick: () => "Pick",
  consoleBgRemove: () => "Remove",
  consoleBgSet: () => "set",
  consoleBgNone: () => "none",
  consoleBgWillRemove: () => "will be removed",
  consoleBgBlur: (n) => `Background blur: ${n}`,
  consoleBgBrightness: (n) => `Background brightness: ${n}`,
  consolePeople: () => "People",
  consoleRoleOwner: () => "owner",
  consoleRoleAdmin: () => "admin",
  consoleMakeAdmin: () => "Make admin",
  consoleRevokeAdmin: () => "Remove admin",
  consoleMakeOwner: () => "Transfer group",
  consoleVoiceMute: () => "Mute",
  consoleVoiceUnmute: () => "Unmute",
  consoleKick: () => "Remove",
  consoleBanDay: () => "Ban for a day",
  consoleBanForever: () => "Ban forever",
  consoleBanned: () => "Banned",
  consoleBanUntil: (when) => `until ${when}`,
  consoleBanPermanent: () => "forever",
  consoleUnban: () => "Unban",

  menuReport: () => "Report",
  reportTitle: () => "Report",
  reportReasonLabel: (key) => {
    switch (key) {
      case "abuse": return "Abuse";
      case "sexual": return "Sexual content";
      case "child": return "Child endangerment";
      case "violence": return "Violence";
      case "illegal": return "Illegal";
      case "spam": return "Spam";
      default: return "Other";
    }
  },
  reportCommentPlaceholder: () => "Comment (optional)",
  reportSend: () => "Send",
  reportSent: () => "Report sent",
  reportsTitle: () => "Reports",
  reportsOpen: (n) => `Open: ${n}`,
  reportsShowResolved: () => "Show closed",
  reportsRemove: () => "Removed",
  reportsDismiss: () => "Dismiss",
  reportsEmpty: () => "No reports",


  menuReact: () => "React",
  menuReply: () => "Reply",
  menuEdit: () => "Edit",
  menuDelete: () => "Delete",
  replyElsewhere: () => "An earlier message",
  replyDeleted: () => "Message deleted",

  svcCallStarted: (who) => `${who} starts a call`,
  svcCallLive: (n) => `Call · ${n} ${plural("en", n, "participant", "participants", "participants")}`,
  svcCallEnded: (d) => (d ? `Call ended · ${d}` : "Call ended"),
  svcJoined: (who) => `${who} joined the group`,
  svcLeft: (who) => `${who} leaves the group`,
  svcRemoved: (who, whom) => `${who} removes ${whom}`,
  svcAdminGranted: (who, whom) => `${who} makes ${whom} an admin`,
  svcAdminRevoked: (who, whom) => `${who} removes ${whom} as admin`,
  svcRenamed: (who, name) => `${who} renames the group to “${name}”`,
  svcBanned: (who, whom, until) =>
    until ? `${who} bans ${whom} until ${until}` : `${who} bans ${whom}`,
  svcUnbanned: (who, whom) => `${who} lifts ${whom}'s ban`,
  svcVoiceMuted: (who, whom, until) =>
    until ? `${who} mutes ${whom} until ${until}` : `${who} mutes ${whom}`,
  svcVoiceUnmuted: (who, whom) => `${who} unmutes ${whom}`,
  svcOwnerChanged: (who, whom) => `${who} hands the group to ${whom}`,
  svcUnknown: () => "Update EchoFloat to see this event",
  svcPreview: (kind) => {
    switch (kind) {
      case "call": return "Call";
      case "joined": return "New member";
      case "left": return "Member left";
      case "removed": return "Member removed";
      case "admin_granted": return "New admin";
      case "admin_revoked": return "Admin revoked";
      case "renamed": return "Group renamed";
      case "banned": return "Ban";
      case "unbanned": return "Ban lifted";
      case "voice_muted": return "Muted";
      case "voice_unmuted": return "Unmuted";
      case "owner_changed": return "New owner";
      default: return "Event";
    }
  },

  desktopOfferTitle: () => "Open in the EchoFloat app?",
  desktopOpen: () => "Open in EchoFloat",
  desktopOpening: () => "Opening EchoFloat…",
  desktopNoApp: () => "No app?",
  desktopInstallTitle: () => "EchoFloat for Windows: calls and chats in a window of their own",
  desktopMissingTitle: () => "EchoFloat doesn't seem to be installed",
  desktopStore: () => "Get it from Microsoft Store",
  desktopInstaller: () => "Download installer",
  desktopHaveIt: () => "Already installed — open",
  desktopDismiss: () => "Stay in the browser",

  durationS: (total) => {
    const m = Math.floor(total / 60);
    const s = total % 60;
    if (m === 0) return `${s}s`;
    return s === 0 ? `${m}m` : `${m}m ${s}s`;
  },
};

export function dict(lang: Lang): Dict {
  return lang === "ru" ? ru : en;
}
